"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LowerUpperMatrixDecomposition = void 0;
var MatrixOperations_1 = require("./MatrixOperations");
/// <summary>
/// LUP-разложение матрицы - это метод факторизации матрицы на произведение трех матриц:
/// матрицы перестановок (P), верхнетреугольной матрицы (U) и нижнетреугольной матрицы (L).
///
/// Суть алгоритма: Матрица A последовательно преобразуется
/// в верхнетреугольную матрицу U с помощью перестановок строк (фиксируемых в P)
/// и вычитания строк для обнуления элементов ниже диагонали.
///
/// Коэффициенты этих вычитаний записываются в матрицу L.
///
/// Применение: эффективное решение систем линейных уравнений и
/// вычисление обратной матрицы
/// </summary>
var LowerUpperMatrixDecomposition = /** @class */ (function () {
    function LowerUpperMatrixDecomposition() {
    }
    // ═══════════════════════════════════════════════════════════
    // Method 1: TryDecompose (full signature)
    // ═══════════════════════════════════════════════════════════
    /// <summary>
    /// Общий метод LU-разложения квадратной матрицы со значениями типа <see cref="double"/>.
    /// </summary>
    /// <param name="src"> Буфер, содержащий исходную матрицу. </param>
    /// <param name="srcStart"> Индекс первого элемента исходной матрицы. </param>
    /// <param name="srcDim"> Размерность матрицы (количество строк и столбцов). </param>
    /// <param name="srcColStride"> Шаг между элементами внутри одной строки исходной матрицы. </param>
    /// <param name="srcRowStride"> Шаг между строками исходной матрицы. </param>
    /// <param name="permutation">
    /// Вектор перестановок строк при декомпозиции.
    /// Заполняется при выполнении метода.
    /// </param>
    /// <param name="dst">
    /// Буфер, содержащий целевую матрицу, состоящую из нижней треугольной и верхней треугольной матриц.
    /// Единичная диагональ нижней треугольной матрицы не хранится.
    /// Заполняется при выполнении метода.
    /// </param>
    /// <param name="dstStart"> Индекс первого элемента целевой матрицы. </param>
    /// <param name="dstColStride"> Шаг между элементами внутри одной строки целевой матрицы. </param>
    /// <param name="dstRowStride"> Шаг между строками целевой матрицы. </param>
    /// <returns>
    /// Признак успешности разложения.
    /// Разложение может завершиться неудачно, если исходная матрица близка к вырожденной.
    /// </returns>
    LowerUpperMatrixDecomposition._tryDecompose = function (src, srcStart, srcDim, srcColStride, srcRowStride, permutation, dst, dstStart, dstColStride, dstRowStride) {
        LowerUpperMatrixDecomposition.validateSquareMatrix(src, srcStart, srcDim, srcColStride, srcRowStride);
        // Копируем данные из src в dst
        // Инициализируем целевую матрицу данными из исходной
        LowerUpperMatrixDecomposition.initializeDst(src, srcStart, srcDim, srcColStride, srcRowStride, dst, dstStart, dstColStride, dstRowStride);
        // Инициализация матрицы перестановок
        for (var i = 0; i < srcDim; i++) {
            permutation[i] = i;
        }
        // Выполняем LU-разложение непосредственно в целевой матрице
        for (var k = 0; k < srcDim; k++) {
            // Поиск главного элемента в текущем столбце целевой матрицы
            var max = 0;
            var maxIndex = k;
            var colValue = k * dstColStride;
            var dstIndex = dstStart + colValue + k * dstRowStride;
            for (var i = k; i < srcDim; i++) {
                var absValue = Math.abs(dst[dstIndex]);
                if (absValue > max) {
                    max = absValue;
                    maxIndex = i;
                }
                dstIndex += dstRowStride;
            }
            // Перестановка строк в целевой матрице, если необходимо
            if (maxIndex !== k) {
                var dstRowValue = dstStart + k * dstRowStride;
                var dstMaxValue = dstStart + maxIndex * dstRowStride;
                // Перестановка строк в целевой матрице
                for (var j = 0; j < srcDim; j++) {
                    var dstIndex1 = dstRowValue + j * dstColStride;
                    var dstIndex2 = dstMaxValue + j * dstColStride;
                    var temp = dst[dstIndex1];
                    dst[dstIndex1] = dst[dstIndex2];
                    dst[dstIndex2] = temp;
                }
                var permTemp = permutation[k];
                permutation[k] = permutation[maxIndex];
                permutation[maxIndex] = permTemp;
            }
            // Формирование одной строки целевой матрицы
            var diagonalValueInv = 1.0 / dst[dstStart + k * dstRowStride + k * dstColStride];
            if (Number.isFinite(diagonalValueInv) && !isNaN(diagonalValueInv)) {
                // Continue processing
            }
            else {
                return false;
            }
            for (var i = k + 1; i < srcDim; i++) {
                var rowIndex = dstStart + i * dstRowStride + k * dstColStride;
                var factor = dst[rowIndex] * diagonalValueInv;
                dst[rowIndex] = factor;
                var targetValue = dstStart + i * dstRowStride;
                var sourceValue = dstStart + k * dstRowStride;
                for (var j = k + 1; j < srcDim; j++) {
                    var targetIndex = targetValue + j * dstColStride;
                    var sourceIndex = sourceValue + j * dstColStride;
                    dst[targetIndex] -= factor * dst[sourceIndex];
                }
            }
        }
        return true;
    };
    // ═══════════════════════════════════════════════════════════
    // Method 3: TryDecompose (minimal params — overload)
    // ═══════════════════════════════════════════════════════════
    /// <summary>
    /// Метод LU-разложения квадратной матрицы со значениями типа <see cref="double"/>
    /// Предполагается, что индекс первого элемента входной (и целевой) матрицы равен 0,
    /// шаг между элементами внутри одной строки равен 1,
    /// шаг между строками исходной матрицы равен размерности матрицы.
    /// </summary>
    /// <param name="src"> Буфер, содержащий исходную матрицу. </param>
    /// <param name="srcDim"> Размерность матрицы (количество строк и столбцов). </param>
    /// <param name="permutation">
    /// Вектор перестановок строк при декомпозиции.
    /// Заполняется при выполнении метода.
    /// </param>
    /// <param name="dst">
    /// Буфер, содержащий целевую матрицу, состоящую из нижней треугольной и верхней треугольной матриц.
    /// Единичная диагональ нижней треугольной матрицы не хранится.
    /// Заполняется при выполнении метода.
    /// </param>
    /// <returns>
    /// Признак успешности разложения.
    /// Разложение может завершиться неудачно, если исходная матрица близка к вырожденной.
    /// </returns>
    LowerUpperMatrixDecomposition.tryDecompose = function (src, srcDim, permutation, dst) {
        return LowerUpperMatrixDecomposition._tryDecompose(src, 0, srcDim, 1, srcDim, permutation, dst, 0, 1, srcDim);
    };
    // ═══════════════════════════════════════════════════════════
    // Method 4: TrySolve (full signature)
    // ═══════════════════════════════════════════════════════════
    /// <summary>
    /// Общий метод вычисления СЛУ с использованием
    /// квадратной матрицы со значениями типа <see cref="double"/>,  к которой применено LU-разложение.
    /// </summary>
    /// <param name="src"> Буфер, содержащий исходную матрицу. </param>
    /// <param name="srcStart"> Индекс первого элемента исходной матрицы. </param>
    /// <param name="srcDim"> Размерность матрицы (количество строк и столбцов). </param>
    /// <param name="srcColStride"> Шаг между элементами внутри одной строки исходной матрицы. </param>
    /// <param name="srcRowStride"> Шаг между строками исходной матрицы. </param>
    /// <param name="b"> Буфер, содержащий вектор-столбец свободных членов </param>
    /// <param name="bStart"> Индекс первого элемента вектора-столбца свободных членов. </param>
    /// <param name="bStride"> Шаг между элементами внутри вектора-столбца свободных членов. </param>
    /// <param name="x">Буфер, содержащий вектор-столбец неизвестных. </param>
    /// <param name="permutation"> Вектор перестановок строк при декомпозиции. </param>>
    /// <param name="solutionBuffer"> Буфер для хранения промежуточных решений. (Его размер должен быть не меньше размерности матрицы). </param>>
    /// <returns>
    /// Признак успешности решения системы.
    /// Решение может завершиться неудачно, если происходит переполнение
    /// или возникает деление на 0.
    /// </returns>
    LowerUpperMatrixDecomposition.trySolve = function (src, srcStart, srcDim, srcColStride, srcRowStride, b, bStart, bStride, x, permutation, solutionBuffer) {
        LowerUpperMatrixDecomposition.validateSquareMatrix(src, srcStart, srcDim, srcColStride, srcRowStride);
        // Решаем L * solutionBuffer = Pb (прямая подстановка)
        for (var i = 0; i < srcDim; i++) {
            var sum = 0;
            var rowValue = srcStart + i * srcRowStride;
            for (var j = 0; j < i; j++) {
                var index = rowValue + j * srcColStride;
                sum += src[index] * solutionBuffer[j];
            }
            solutionBuffer[i] = b[bStart + permutation[i] * bStride] - sum; // выполняется инициализация только выбранных элементов вектора b и их перестановка
        }
        // Решаем U * x = solutionBuffer (обратная подстановка)
        for (var i = srcDim - 1; i >= 0; i--) {
            var sum = 0;
            var rowValue = srcStart + i * srcRowStride;
            for (var j = i + 1; j < srcDim; j++) {
                var index = rowValue + j * srcColStride;
                sum += src[index] * x[j];
            }
            var diagonalIndex = rowValue + i * srcColStride;
            var solutionValue = (solutionBuffer[i] - sum) / src[diagonalIndex];
            if (!Number.isFinite(solutionValue))
                return false;
            x[i] = solutionValue;
        }
        return true;
    };
    // ═══════════════════════════════════════════════════════════
    // Private helpers (needed by methods 1-5)
    // ═══════════════════════════════════════════════════════════
    /// <summary>
    /// Инициализация целевой матрицы данными из исходной согласно стартовым индексам,
    /// размерности, шагам между элементами внутри одной строки и между строками.
    /// </summary>
    /// <param name="src"> Буфер, содержащий исходную матрицу. </param>
    /// <param name="srcStart"> Индекс первого элемента исходной матрицы. </param>
    /// <param name="srcDim"> Размерность матрицы (количество строк и столбцов). </param>
    /// <param name="srcRowStride"> Шаг между элементами внутри одной строки исходной матрицы. </param>
    /// <param name="srcColStride"> Шаг между строками исходной матрицы. </param>
    /// <param name="dst">
    /// Пустой буфер, который заполняется элементами целевой матрицы.
    /// </param>
    /// <param name="dstStart"> Индекс первого элемента целевой матрицы. </param>
    /// <param name="dstRowStride"> Шаг между элементами внутри одной строки целевой матрицы. </param>
    /// <param name="dstColStride"> Шаг между строками целевой матрицы. </param>
    /// <returns>
    /// Буфер, содержащий целевую матрицу.
    /// </returns>
    LowerUpperMatrixDecomposition.initializeDst = function (src, srcStart, srcDim, srcColStride, srcRowStride, dst, dstStart, dstColStride, dstRowStride) {
        // Копируем только необходимые элементы
        for (var i = 0; i < srcDim; i++) {
            var dstRowValue = i * dstRowStride;
            var srcRowValue = i * srcRowStride;
            for (var j = 0; j < srcDim; j++) {
                var srcIndex = srcStart + srcRowValue + j * srcColStride;
                var dstIndex = dstStart + dstRowValue + j * dstColStride;
                dst[dstIndex] = src[srcIndex];
            }
        }
    };
    // ═══════════════════════════════════════════════════════════
    // Method 15: ValidateSquareMatrix (public)
    // ═══════════════════════════════════════════════════════════
    /// <summary>
    /// Метод проверки матрицы на отсутствие выходов за пределы
    /// буфера с помощью оценки доступности угловых элементов матрицы.
    /// </summary>
    /// <param name="src"> Буфер, содержащий исходную матрицу. </param>
    /// <param name="srcStart"> Индекс первого элемента исходной матрицы. </param>
    /// <param name="srcDim"> Размерность матрицы (количество строк и столбцов). </param>
    /// <param name="srcRowStride"> Шаг между элементами внутри одной строки исходной матрицы. </param>
    /// <param name="srcColStride"> Шаг между строками исходной матрицы. </param>
    /// <returns>
    /// Признак успешности получения угловых элементов матрицы.
    /// Может завершиться неудачно, если хотя бы один из
    /// элементов оказался недоступен или происходит выход за пределы буфера.
    /// </returns>
    LowerUpperMatrixDecomposition.validateSquareMatrix = function (src, srcStart, srcDim, srcColStride, srcRowStride) {
        MatrixOperations_1.MatrixOperations.validateMatrix(src, srcStart, srcDim, srcDim, srcColStride, srcRowStride);
    };
    // ═══════════════════════════════════════════════════════════
    // Method 18: GCD (private)
    // ═══════════════════════════════════════════════════════════
    /// <summary>
    /// Метод вычисления наибольшего общего делителя
    /// </summary>
    /// <param name="a"> Число a</param>
    /// <param name="b"> Число b</param>
    /// <returns>
    /// НОД(а,b)
    /// </returns>
    LowerUpperMatrixDecomposition.gcd = function (a, b) {
        if (b === 0)
            return a;
        return LowerUpperMatrixDecomposition.gcd(b, a % b);
    };
    return LowerUpperMatrixDecomposition;
}());
exports.LowerUpperMatrixDecomposition = LowerUpperMatrixDecomposition;
